import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import Fuse from 'fuse.js'
import {
  MEXICAN_MUSIC_STYLES,
  getAllSearchableItems,
  type MusicStyle,
  MexicanInstrument,
  INSTRUMENT_DESCRIPTIONS,
  VocalStyle,
  VOCAL_STYLE_DESCRIPTIONS,
  INSTRUMENT_PRESETS,
  type SearchableMusicItem,
} from '@/consts/es-music-consts'
import {
  CARIBBEAN_CONTEMPORARY_MUSIC_STYLES,
  getAllCombinedSearchableItems,
} from '@/consts/es-music-alt-consts'

export const Route = createFileRoute('/app/music-styles')({
  component: MusicStylesPage,
})

function MusicStylesPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'styles' | 'instruments' | 'vocals' | 'presets'>('all')
  const [showCaribbean, setShowCaribbean] = useState(false)
  const parentRef = useRef<HTMLDivElement>(null)

  // Get searchable items based on filter
  const searchableItems = useMemo(() => {
    if (showCaribbean) {
      return getAllCombinedSearchableItems()
    }
    return getAllSearchableItems()
  }, [showCaribbean])

  // Setup Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(searchableItems, {
      keys: ['name', 'description', 'keywords'],
      threshold: 0.3,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 1,
    })
  }, [searchableItems])

  // Filter results
  const filteredResults = useMemo(() => {
    let results: SearchableMusicItem[]

    if (!searchQuery.trim()) {
      // Show all items when no search
      results = searchableItems
    } else {
      const fuseResults = fuse.search(searchQuery)
      results = fuseResults.map((result) => result.item)
    }

    // Apply type filter
    if (filterType !== 'all') {
      const typeMap: Record<typeof filterType, SearchableMusicItem['type']> = {
        styles: 'style',
        instruments: 'instrument',
        vocals: 'vocal',
        presets: 'preset',
      }
      results = results.filter((item) => item.type === typeMap[filterType])
    }

    return results
  }, [searchQuery, filterType, fuse, searchableItems])

  // Virtualizer for the list
  const virtualizer = useVirtualizer({
    count: filteredResults.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120,
    overscan: 5,
  })

  const getItemDetails = (item: SearchableMusicItem) => {
    if (item.type === 'style') {
      const style = showCaribbean
        ? CARIBBEAN_CONTEMPORARY_MUSIC_STYLES.find((s) => s.name === item.name) ||
          MEXICAN_MUSIC_STYLES.find((s) => s.name === item.name)
        : MEXICAN_MUSIC_STYLES.find((s) => s.name === item.name)
      return style
    }
    if (item.type === 'instrument') {
      const instrument = Object.values(MexicanInstrument).find((i) => i === item.name)
      return instrument ? INSTRUMENT_DESCRIPTIONS[instrument] : null
    }
    if (item.type === 'vocal') {
      const vocal = Object.values(VocalStyle).find((v) => v === item.name)
      return vocal ? VOCAL_STYLE_DESCRIPTIONS[vocal] : null
    }
    if (item.type === 'preset') {
      return INSTRUMENT_PRESETS.find((p) => p.name === item.name)
    }
    return null
  }

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-8 terminal-border p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [MUSIC STYLES BROWSER]
          </h1>
          <p className="text-trail-tan font-mono text-sm sm:text-base">
            Search and explore Mexican and Caribbean music styles, instruments, and vocal styles
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Input */}
          <div className="terminal-border p-4 bg-black">
            <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
              ► SEARCH MUSIC STYLES, INSTRUMENTS, VOCALS
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for mariachi, trumpet, bolero, accordion..."
              className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 font-bold font-mono text-sm border-2 transition-all ${
                filterType === 'all'
                  ? 'bg-sky-blue text-black border-sky-blue'
                  : 'bg-black text-sky-blue border-sky-blue hover:bg-sky-blue hover:text-black'
              }`}
            >
              ALL
            </button>
            <button
              onClick={() => setFilterType('styles')}
              className={`px-4 py-2 font-bold font-mono text-sm border-2 transition-all ${
                filterType === 'styles'
                  ? 'bg-terminal-green text-black border-terminal-green'
                  : 'bg-black text-terminal-green border-terminal-green hover:bg-terminal-green hover:text-black'
              }`}
            >
              STYLES
            </button>
            <button
              onClick={() => setFilterType('instruments')}
              className={`px-4 py-2 font-bold font-mono text-sm border-2 transition-all ${
                filterType === 'instruments'
                  ? 'bg-trail-tan text-black border-trail-tan'
                  : 'bg-black text-trail-tan border-trail-tan hover:bg-trail-tan hover:text-black'
              }`}
            >
              INSTRUMENTS
            </button>
            <button
              onClick={() => setFilterType('vocals')}
              className={`px-4 py-2 font-bold font-mono text-sm border-2 transition-all ${
                filterType === 'vocals'
                  ? 'bg-purple-500 text-white border-purple-500'
                  : 'bg-black text-purple-500 border-purple-500 hover:bg-purple-500 hover:text-white'
              }`}
            >
              VOCALS
            </button>
            <button
              onClick={() => setFilterType('presets')}
              className={`px-4 py-2 font-bold font-mono text-sm border-2 transition-all ${
                filterType === 'presets'
                  ? 'bg-yellow-500 text-black border-yellow-500'
                  : 'bg-black text-yellow-500 border-yellow-500 hover:bg-yellow-500 hover:text-black'
              }`}
            >
              PRESETS
            </button>
            <button
              onClick={() => setShowCaribbean(!showCaribbean)}
              className={`px-4 py-2 font-bold font-mono text-sm border-2 transition-all ${
                showCaribbean
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-black text-green-400 border-green-400 hover:bg-green-600 hover:text-white'
              }`}
            >
              {showCaribbean ? '✓ CARIBBEAN' : '+ CARIBBEAN'}
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-trail-tan font-mono text-sm">
          Found {filteredResults.length} {filterType === 'all' ? 'items' : filterType}
        </div>

        {/* Results List */}
        <div
          ref={parentRef}
          className="terminal-border p-4 bg-black"
          style={{ height: '600px', overflow: 'auto' }}
        >
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const item = filteredResults[virtualItem.index]
              const details = getItemDetails(item)

              return (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                  className="mb-4"
                >
                  <div className="bg-black border-2 border-sky-blue p-4 hover:border-terminal-green transition-all">
                    {/* Item Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-terminal-green font-mono mb-1">
                          {item.name}
                        </h3>
                        <span className="text-sky-blue text-xs font-mono uppercase tracking-wider">
                          [{item.type}]
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-trail-tan font-mono text-sm mb-3">{item.description}</p>

                    {/* Style Details */}
                    {item.type === 'style' && details && (
                      <div className="space-y-2 text-xs font-mono">
                        <div>
                          <span className="text-sky-blue">Instruments:</span>{' '}
                          <span className="text-white">
                            {(details as MusicStyle).instruments.join(', ')}
                          </span>
                        </div>
                        {(details as MusicStyle).vocalStyle && (
                          <div>
                            <span className="text-sky-blue">Vocal Style:</span>{' '}
                            <span className="text-white">{(details as MusicStyle).vocalStyle}</span>
                          </div>
                        )}
                        {(details as MusicStyle).tempo && (
                          <div>
                            <span className="text-sky-blue">Tempo:</span>{' '}
                            <span className="text-white">{(details as MusicStyle).tempo}</span>
                          </div>
                        )}
                        {(details as MusicStyle).timeSignature && (
                          <div>
                            <span className="text-sky-blue">Time Signature:</span>{' '}
                            <span className="text-white">
                              {(details as MusicStyle).timeSignature}
                            </span>
                          </div>
                        )}
                        {(details as MusicStyle).region && (
                          <div>
                            <span className="text-sky-blue">Region:</span>{' '}
                            <span className="text-white">{(details as MusicStyle).region}</span>
                          </div>
                        )}
                        {(details as MusicStyle).era && (
                          <div>
                            <span className="text-sky-blue">Era:</span>{' '}
                            <span className="text-white">{(details as MusicStyle).era}</span>
                          </div>
                        )}
                        {(details as MusicStyle).publicDomainSongs &&
                          (details as MusicStyle).publicDomainSongs!.length > 0 && (
                            <div>
                              <span className="text-sky-blue">Public Domain Songs:</span>{' '}
                              <span className="text-white">
                                {(details as MusicStyle).publicDomainSongs!.join(', ')}
                              </span>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Preset Details */}
                    {item.type === 'preset' && details && (
                      <div className="space-y-2 text-xs font-mono">
                        <div>
                          <span className="text-sky-blue">Style:</span>{' '}
                          <span className="text-white">
                            {(details as typeof INSTRUMENT_PRESETS[0]).style}
                          </span>
                        </div>
                        <div>
                          <span className="text-sky-blue">Instruments:</span>{' '}
                          <span className="text-white">
                            {(details as typeof INSTRUMENT_PRESETS[0]).instruments.join(', ')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

